import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { errMsg } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Globe, ClipboardPaste, Loader2, Check, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useEffect } from "react";

export default function Onboarding() {
  const [mode, setMode] = useState("industry"); // industry | choose | website | paste | review
  const [industries, setIndustries] = useState([]);
  const [industry, setIndustry] = useState("");
  const [website, setWebsite] = useState("");
  const [pasted, setPasted] = useState("");
  const [loading, setLoading] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const nav = useNavigate();
  const { setUser, user } = useAuth();

  useEffect(() => { api.get("/industries").then(r => setIndustries(r.data)); }, []);

  const pickIndustry = async (key) => {
    setIndustry(key);
    setLoading(true);
    try { await api.patch("/business", { industry: key }); }
    catch { toast.error("Couldn't save industry selection — continuing anyway."); }
    setLoading(false);
    setMode("choose");
  };

  const doExtract = async (payload) => {
    setLoading(true);
    try {
      const r = await api.post("/onboarding/extract", payload);
      const ex = r.data.extracted || {};
      ex.services = (ex.services || []).map((s) => ({ ...s, _k: crypto.randomUUID() }));
      setExtracted(ex);
      setMode("review");
    } catch (e) {
      toast.error(errMsg(e, "Could not import — try pasting details instead."));
    } finally { setLoading(false); }
  };

  const apply = async () => {
    setLoading(true);
    try {
      await api.post("/onboarding/apply", { extracted });
      toast.success("Business info imported");
      nav("/app");
    } catch (e) { toast.error(errMsg(e, "Failed to save")); }
    finally { setLoading(false); }
  };

  const skip = async () => {
    // just seed demo & go to dashboard
    try { await api.post("/demo/seed"); toast.success("Demo business loaded"); nav("/app"); }
    catch { toast.error("Failed"); }
  };

  const updateField = (k, v) => setExtracted({ ...extracted, [k]: v });
  const updateSvc = (i, k, v) => {
    const svcs = [...(extracted.services || [])];
    svcs[i] = { ...svcs[i], [k]: v };
    setExtracted({ ...extracted, services: svcs });
  };
  const addSvc = () => setExtracted({ ...extracted, services: [...(extracted.services || []), { name: "", price: 0, duration_min: 30, _k: crypto.randomUUID() }] });
  const removeSvc = (i) => setExtracted({ ...extracted, services: (extracted.services || []).filter((_, idx) => idx !== i) });

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-xl bg-slate-950 text-white flex items-center justify-center font-bold">A</div>
          <span className="font-heading font-extrabold text-xl tracking-tight">ASFA</span>
        </div>

        {mode === "industry" && (
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-950">What kind of business is this?</h1>
            <p className="mt-3 text-slate-600 text-lg">Your receptionist learns the vocabulary, questions, and workflows for your industry.</p>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-3 items-stretch" data-testid="industry-grid">
              {industries.map(i => (
                <button key={i.key} data-testid={`industry-${i.key}`} onClick={() => pickIndustry(i.key)}
                  disabled={loading}
                  className="text-left bg-white border border-slate-200 rounded-2xl p-5 hover:border-blue-500 hover:shadow-md transition-all h-full">
                  <div className="font-heading font-semibold">{i.name}</div>
                  <div className="text-xs text-slate-500 mt-1">{i.description}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {mode === "choose" && (
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-950">Let's set up your AI receptionist</h1>
            <p className="mt-3 text-slate-600 text-lg">Tell ASFA about your business — we'll do the rest.</p>
            <div className="mt-10 grid md:grid-cols-2 gap-5">
              <button data-testid="onboarding-website-card" onClick={() => setMode("website")}
                className="text-left bg-white border border-slate-200 rounded-2xl p-6 hover:border-blue-500 hover:shadow-md transition-all">
                <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center mb-4"><Globe className="w-5 h-5 text-blue-600" /></div>
                <div className="font-heading font-semibold text-lg">Import from website</div>
                <div className="text-sm text-slate-600 mt-1.5">Fastest. We'll read your site and pull out everything.</div>
              </button>
              <button data-testid="onboarding-paste-card" onClick={() => setMode("paste")}
                className="text-left bg-white border border-slate-200 rounded-2xl p-6 hover:border-blue-500 hover:shadow-md transition-all">
                <div className="w-11 h-11 rounded-xl bg-purple-50 flex items-center justify-center mb-4"><ClipboardPaste className="w-5 h-5 text-purple-600" /></div>
                <div className="font-heading font-semibold text-lg">Paste your info</div>
                <div className="text-sm text-slate-600 mt-1.5">Paste anything you have — website text, notes, service list.</div>
              </button>
            </div>
            <div className="mt-6 text-center">
              <button data-testid="onboarding-demo-btn" onClick={skip} className="text-sm text-slate-500 hover:text-slate-900 underline">
                Or start with a demo {industries.find(i => i.key === industry)?.name.toLowerCase() || ""} business →
              </button>
            </div>
          </div>
        )}

        {mode === "website" && (
          <div className="bg-white border border-slate-200 rounded-2xl p-8">
            <button onClick={() => setMode("choose")} className="text-sm text-slate-500 mb-4">← Back</button>
            <h2 className="text-2xl font-bold tracking-tight">What's your business website?</h2>
            <p className="text-slate-600 mt-1.5">We'll fetch it and let AI extract your services, hours, and prices.</p>
            <div className="mt-6">
              <Label htmlFor="site">Website URL</Label>
              <Input data-testid="onboarding-website-input" id="site" placeholder="https://yourbarbershop.com"
                value={website} onChange={(e) => setWebsite(e.target.value)} className="mt-1.5 h-12" />
            </div>
            <div className="mt-4">
              <Label htmlFor="extra">Anything else? (optional)</Label>
              <Textarea data-testid="onboarding-extra-notes" id="extra" rows={4} value={pasted}
                onChange={(e) => setPasted(e.target.value)} className="mt-1.5"
                placeholder="Add anything not on your site — extra services, policies, notes…" />
            </div>
            <Button data-testid="onboarding-website-submit" disabled={!website || loading}
              onClick={() => doExtract({ website, text: pasted || undefined })}
              className="mt-6 rounded-xl bg-blue-600 hover:bg-blue-700 h-11 px-6">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…</> : <><Sparkles className="w-4 h-4 mr-2" /> Import my business</>}
            </Button>
          </div>
        )}

        {mode === "paste" && (
          <div className="bg-white border border-slate-200 rounded-2xl p-8">
            <button onClick={() => setMode("choose")} className="text-sm text-slate-500 mb-4">← Back</button>
            <h2 className="text-2xl font-bold tracking-tight">Paste anything about your business</h2>
            <p className="text-slate-600 mt-1.5">Website text, service list, FAQs — whatever you have.</p>
            <Textarea data-testid="onboarding-paste-input" rows={12} value={pasted}
              onChange={(e) => setPasted(e.target.value)} className="mt-5"
              placeholder="We're Northside Barbers at 123 Main St. Open Mon-Fri 9-7, Sat 9-5. Haircut $30, Beard trim $20…" />
            <Button data-testid="onboarding-paste-submit" disabled={!pasted || loading}
              onClick={() => doExtract({ text: pasted })}
              className="mt-5 rounded-xl bg-blue-600 hover:bg-blue-700 h-11 px-6">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Extracting…</> : <><Sparkles className="w-4 h-4 mr-2" /> Extract with AI</>}
            </Button>
          </div>
        )}

        {mode === "review" && extracted && (
          <div>
            <div className="flex items-center gap-2 text-emerald-600 mb-3"><Check className="w-5 h-5" /><span className="text-sm font-semibold">We found this about your business</span></div>
            <h2 className="text-3xl font-bold tracking-tight">Review & confirm</h2>
            <p className="text-slate-600 mt-1">Edit anything that isn't right.</p>

            <div className="mt-8 space-y-5">
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="font-heading font-semibold text-lg mb-4">Business</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Name</Label><Input data-testid="review-name" value={extracted.name || ""} onChange={(e) => updateField("name", e.target.value)} className="mt-1.5" /></div>
                  <div><Label>Phone</Label><Input data-testid="review-phone" value={extracted.phone || ""} onChange={(e) => updateField("phone", e.target.value)} className="mt-1.5" /></div>
                  <div className="sm:col-span-2"><Label>Address</Label><Input data-testid="review-address" value={extracted.address || ""} onChange={(e) => updateField("address", e.target.value)} className="mt-1.5" /></div>
                  <div className="sm:col-span-2"><Label>Description</Label><Textarea data-testid="review-description" rows={3} value={extracted.description || ""} onChange={(e) => updateField("description", e.target.value)} className="mt-1.5" /></div>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="font-heading font-semibold text-lg mb-4">Services</div>
                <div className="space-y-3">
                  {(extracted.services || []).map((s, i) => (
                    <div key={s._k} className="grid grid-cols-12 gap-2 items-center">
                      <Input data-testid={`review-service-${i}-name`} className="col-span-5" placeholder="Name" value={s.name || ""} onChange={(e) => updateSvc(i, "name", e.target.value)} />
                      <Input data-testid={`review-service-${i}-price`} className="col-span-3" type="number" placeholder="Price" value={s.price || 0} onChange={(e) => updateSvc(i, "price", parseFloat(e.target.value) || 0)} />
                      <Input data-testid={`review-service-${i}-duration`} className="col-span-3" type="number" placeholder="Min" value={s.duration_min || 30} onChange={(e) => updateSvc(i, "duration_min", parseInt(e.target.value) || 30)} />
                      <button onClick={() => removeSvc(i)} className="col-span-1 text-slate-400 hover:text-red-500 text-sm">✕</button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addSvc} data-testid="review-add-service" className="rounded-xl">+ Add service</Button>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setMode("choose")} className="rounded-xl">Start over</Button>
                <Button data-testid="review-continue-btn" onClick={apply} disabled={loading} className="rounded-xl bg-blue-600 hover:bg-blue-700 h-11 px-6">
                  {loading ? "Saving…" : <>Looks good — continue <ArrowRight className="w-4 h-4 ml-2" /></>}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
